/* Shared link contract. Affiliate attribution remains owned by Simple Affiliate. */
(function () {
  'use strict';
  const drafts = new Map();
  const WORKER_URL = 'https://uploads.tablereadycards.com';
  // Link Creator landing (deck=, deck_list=). The upload Worker serves deck=
  // records; every fetch has a timeout and every wait ends in a message.
  const LIST_URL_LIMIT = 6000; // characters of the encoded deck_list value
  const LIST_RECORD_LIMIT = 96 * 1024;
  const RECORD_TIMEOUT_MS = 10000;
  const COPY = {
    loading: 'Loading the deck from your link\u2026',
    unavailable: 'This deck link is no longer available. You can still paste a list or a deck URL.',
    failed: 'We could not load this deck link right now. Refresh the page to try again, or paste a list or a deck URL.',
    tooLong: 'This deck list is too long for a link. You can still paste the list or use a deck URL.',
    sizing: 'Choosing the print option that fits this deck\u2026'
  };
  // Precedence: deck, then deck_list, then deck_url (read). null when neither list param is present.
  function readList(href) {
    const page = new URL(href);
    const id = (page.searchParams.get('deck') || '').trim();
    if (id) return /^[A-Za-z0-9_-]{1,64}$/.test(id) ? {kind: 'deck', id: id.toLowerCase()} : {kind: 'invalid'};
    if (!page.searchParams.has('deck_list')) return null;
    const raw = /[?&]deck_list=([^&#]*)/.exec(page.search);
    if (raw && raw[1].length > LIST_URL_LIMIT) return {kind: 'too-long'};
    const text = (page.searchParams.get('deck_list') || '').replace(/\r\n?/g, '\n').trim();
    if (!text) return null;
    return text.length > LIST_URL_LIMIT ? {kind: 'too-long'} : {kind: 'list', text};
  }
  function read(href) {
    const page = new URL(href);
    if ((page.searchParams.get('deck') || '').trim() || (page.searchParams.get('deck_list') || '').trim()) return null;
    const url = (page.searchParams.get('deck_url') || '').trim();
    if (!url || url.length > 2048) return null;
    return {url, auto: page.searchParams.get('deck_route') === 'auto',
      contents: page.searchParams.get('deck_contents') === 'all' ? 'Mainboard + Sideboard' : 'Mainboard'};
  }
  function supportedURL(value) {
    if (value.length > 2048) return false;
    try {
      const url = new URL(value), host = url.hostname.replace(/^www\./, '');
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return false;
      return (host === 'moxfield.com' && /^\/decks\/[A-Za-z0-9._~-]+\/?$/.test(url.pathname))
        || (host === 'archidekt.com' && /^\/decks\/\d+(?:\/[^/]*)?\/?$/.test(url.pathname))
        || (host === 'cubecobra.com' && /^\/cube\/(?:list|overview)\/[A-Za-z0-9._~-]+\/?$/.test(url.pathname))
        || (host === 'topdeck.gg' && /^\/deck\/[A-Za-z0-9._~-]+\/(?:@|%40)?[A-Za-z0-9._~-]+\/?$/i.test(url.pathname));
    } catch (_) { return false; }
  }
  function productURL(value, origin) {
    const url = new URL(value, origin);
    if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?products\/[^/]+\/?$/.test(url.pathname)) {
      throw new Error('Choose an available custom product.');
    }
    return url;
  }
  function selectVariant(product, count) {
    const variants = (product.variants || []).filter(v => v.available && !/fireball|foil/i.test(v.title));
    if (product.exact) return variants[0]?.id || '';
    const candidates = variants.map(variant => {
      const match = /(?:up to\s+)?(\d+)\s*cards?/i.exec(variant.title) || /(?:up to\s+)?(\d+)[ -]*cards?/i.exec(product.title);
      const maximum = Number(variant.maximum) || Number(match?.[1]) || 0;
      return {...variant, maximum};
    }).filter(v => count >= Number(v.minimum || 1) && count <= v.maximum).sort((a,b) => a.maximum - b.maximum);
    return candidates[0]?.id || '';
  }
  function destination(href, product, result) {
    const current = new URL(href);
    const target = productURL(product.url, current.origin);
    const variant = selectVariant(product, result.mainboardCount + result.sideboardCount);
    if (!variant) throw new Error('No available print option fits this deck. Please contact us.');
    target.search = current.search; // sacode, discounts, UTM and preview parameters survive.
    target.searchParams.set('variant', variant);
    target.searchParams.delete('view');
    target.searchParams.delete('deck_route'); // One routing pass; never redirect in a loop.
    target.searchParams.set('deck_url', result.url);
    target.searchParams.set('deck_contents', result.contents === 'Mainboard + Sideboard' ? 'all' : 'main');
    return target.href;
  }
  function build(href, product, source, sacode, auto) {
    const origin = new URL(href).origin;
    const target = productURL(product, origin);
    // Shopify's editor decorates product URLs with its preview session.
    // Generated customer links must never carry that session or preview state.
    target.search = '';
    target.hash = '';
    target.searchParams.set('deck_url', source.trim());
    if (sacode.trim()) target.searchParams.set('sacode', sacode.trim());
    if (auto) target.searchParams.set('deck_route', 'auto');
    // The theme editor can instrument URL.href and anchor.href for navigation.
    // Assemble share text without invoking either editor navigation hook.
    return origin + target.pathname + '?' + target.searchParams.toString();
  }
  // Cards in a pasted list, counted like the product page (TRCDeckInput's
  // cleanDecklistText and section headers). null when a line has no quantity.
  function countList(text) {
    const feedback = window.TRCDeckFeedback;
    if (feedback && typeof feedback.tallyDecklist === 'function') {
      const tally = feedback.tallyDecklist(text);
      return tally.invalidLines || !tally.total ? null : tally.total;
    }
    const input = window.TRCDeckInput || {};
    const header = input.DECKLIST_SECTION_HEADER || /^(?:commanders?|companions?|deck|main(?:\s*(?:deck|board))?|sideboard|side\s+board|sb|maybe(?:\s*board)?|considering)\s*:?\s*(?:\(\s*\d+\s*\))?\s*:?$/i;
    const clean = typeof input.cleanDecklistText === 'function' ? input.cleanDecklistText(text) : String(text || '');
    let total = 0;
    for (const raw of clean.split('\n')) {
      const line = raw.trim();
      if (!line || /^\/\//.test(line) || header.test(line)) continue;
      const match = /^(\d+)\s*x?\s+\S/i.exec(line);
      if (!match) return null;
      total += parseInt(match[1], 10);
    }
    return total || null;
  }
  // The print options of the page's own product (deckInputConfig), keeping the
  // current option's other choices (print type) and changing only the size.
  function pageProduct(config, current) {
    const titles = config && config.VARIANT_TITLES;
    if (!titles || !config.DECK_MAX_BY_VARIANT) return null;
    const rest = title => String(title || '').split(' / ').filter(part => !/\d+[ -]*cards?/i.test(part)).join(' / ');
    const same = rest(titles[current]);
    const variants = Object.keys(titles).filter(id => rest(titles[id]) === same).map(id => ({id, title: titles[id], available: true,
      maximum: Number(config.DECK_MAX_BY_VARIANT[id]) || 0, minimum: Number((config.DECK_MIN_BY_VARIANT || {})[id]) || 1}));
    return {title: config.PRODUCT_TITLE || '', variants};
  }
  // The variant a list link should open on, or '' to keep the page's own.
  // A variant in the URL always wins; then the link's saved option; then the
  // smallest option on this product that fits the list.
  // The print option a deck= record names, when it is one of this page's
  // variants and the URL names none ('' otherwise).
  function recordVariant(href, config, current, record) {
    if (new URL(href).searchParams.get('variant') || !config || !current) return '';
    if (record && record.variantId && config.VARIANT_TITLES && Object.prototype.hasOwnProperty.call(config.VARIANT_TITLES, record.variantId)) {
      return record.variantId === current ? '' : record.variantId;
    }
    return '';
  }
  function landingVariant(href, config, current, text, record) {
    if (new URL(href).searchParams.get('variant') || !config || !current) return '';
    if (record && record.variantId && config.VARIANT_TITLES && Object.prototype.hasOwnProperty.call(config.VARIANT_TITLES, record.variantId)) {
      return recordVariant(href, config, current, record);
    }
    const count = countList(text);
    const product = count && pageProduct(config, current);
    const pick = product ? selectVariant(product, count) : '';
    return pick && pick !== current ? pick : '';
  }
  function withVariant(href, variant) {
    const url = new URL(href);
    url.searchParams.set('variant', variant); // sacode, discount, UTM and preview parameters stay.
    return url.href;
  }
  function validRecord(body) {
    if (!body || body.ok !== true || typeof body.decklist !== 'string') return null;
    const decklist = body.decklist.replace(/\r\n?/g, '\n').trim();
    if (!decklist || decklist.length > LIST_RECORD_LIMIT) return null;
    const board = body.board === 'Mainboard + Sideboard' ? body.board : 'Mainboard';
    return {decklist, board, productHandle: typeof body.productHandle === 'string' ? body.productHandle : '',
      variantId: body.variantId == null || body.variantId === '' ? '' : String(body.variantId)};
  }
  // Resolves {record} | {missing: true} | {error}; never rejects, never hangs.
  function fetchRecord(id, timeoutMs) {
    return new Promise(resolve => {
      let done = false;
      let controller = null;
      try { controller = typeof AbortController === 'function' ? new AbortController() : null; } catch (_) { controller = null; }
      const timer = setTimeout(() => { try { if (controller) controller.abort(); } catch (_) {} finish({error: 'timeout'}); }, timeoutMs || RECORD_TIMEOUT_MS);
      function finish(value) { if (done) return; done = true; clearTimeout(timer); resolve(value); }
      let request;
      try {
        const init = {method: 'GET', credentials: 'omit', headers: {Accept: 'application/json'}};
        if (controller) init.signal = controller.signal;
        request = Promise.resolve(window.fetch(WORKER_URL + '/?type=deck-link&id=' + encodeURIComponent(id), init));
      } catch (_) { finish({error: 'network'}); return; }
      request.then(response => Promise.resolve(response.json()).catch(() => undefined).then(body => {
        if (response.status === 404 || response.status === 410 || (body && body.ok === false && body.code === 'not_found')) return finish({missing: true});
        // An answer that is not JSON (a captive portal or a network error page) is a failed load, not a missing link.
        if (!response.ok || body === undefined) return finish({error: 'http'});
        const record = validRecord(body);
        finish(record ? {record} : {missing: true});
      }), () => finish({error: 'network'}));
    });
  }
  function noticeFor(root) {
    let notice = root.querySelector('[data-deck-link-notice]');
    if (notice) return notice;
    notice = document.createElement('p');
    notice.className = 'deck-input__link-validation';
    notice.setAttribute('data-deck-link-notice', '');
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.hidden = true;
    const anchor = root.querySelector('.deck-input__toggle') || root.querySelector('.deck-input__radio');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(notice, anchor);
    else root.insertBefore(notice, root.firstChild);
    return notice;
  }
  function showNotice(root, state, message) {
    const notice = noticeFor(root);
    notice.className = 'deck-input__link-validation' + (state ? ' is-' + state : '');
    notice.textContent = message || '';
    notice.hidden = !message;
  }
  function listDraftKey(link) {
    return location.pathname + '\n' + (link.kind === 'deck' ? 'deck:' + link.id : 'list:' + link.text);
  }
  // deck= and deck_list= links: fill the Paste a list tab and let the page run
  // its normal text check. Always returns null: deck_url is not used.
  function setupList(root, link, controls) {
    const textarea = controls.textarea || root.querySelector('textarea[name="properties[Decklist]"]');
    if (!textarea) return null;
    if (link.kind === 'invalid') { showNotice(root, 'error', COPY.unavailable); return null; }
    if (link.kind === 'too-long') { showNotice(root, 'error', COPY.tooLong); return null; }
    const key = listDraftKey(link);
    const saved = history.state && history.state.trcDeckList;
    const prior = drafts.get(key) || (saved && saved.key === key ? saved : null);
    let historyTimer = null;
    function remember() {
      const draft = {key, text: textarea.value};
      drafts.set(key, draft);
      if (historyTimer) clearTimeout(historyTimer);
      // Safari limits replaceState calls; write once typing pauses.
      historyTimer = setTimeout(() => {
        historyTimer = null;
        try { history.replaceState(Object.assign({}, history.state, {trcDeckList: draft}), '', location.href); } catch (_) { /* In-memory restoration still works. */ }
      }, 400);
    }
    function trackEdits() {
      root.addEventListener('input', event => { if (event.target === textarea) remember(); }, {signal: controls.signal});
    }
    function useTextMode() {
      const radio = root.querySelector('.deck-input__radio[data-mode="text"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', {bubbles: true}));
      }
      if (controls.setMode && controls.mode && controls.mode() !== 'text') controls.setMode('text');
    }
    function fill(text, record, sync) {
      if (record && controls.sideboard && !controls.sideboard.hasAttribute('data-deck-contents-by-variant')) controls.sideboard.value = record.board;
      const cleaner = window.TRCDeckInput && window.TRCDeckInput.cleanDecklistText;
      textarea.value = typeof cleaner === 'function' ? cleaner(text) : text;
      // During setup the page applies the tab and checks the list right after;
      // later (a deck= record), switch the tab and check it the way pasting does.
      if (sync) controls.setMode('text');
      else { useTextMode(); textarea.dispatchEvent(new Event('input', {bubbles: true})); }
      trackEdits();
      remember();
    }
    function sized(text, record) {
      const current = textarea.form && textarea.form.querySelector('[name="id"]');
      // Any Quantity pages size the order themselves: only a record's own
      // print option (a finish) is applied there, never a size pick.
      const variant = root.querySelector('[data-exact-quantity]') ? recordVariant(location.href, controls.config, current && current.value, record)
        : landingVariant(location.href, controls.config, current && current.value, text, record);
      if (!variant) return false;
      showNotice(root, 'loading', COPY.sizing);
      api.navigate(withVariant(location.href, variant));
      return true;
    }
    if (prior) {
      // Back, reload or a replaced section: the shopper's own edits win.
      if (!textarea.value) textarea.value = prior.text;
      controls.setMode('text');
      trackEdits();
      return null;
    }
    if (link.kind === 'list') {
      if (!sized(link.text, null)) fill(link.text, null, true);
      return null;
    }
    const startMode = controls.mode ? controls.mode() : '';
    const startURL = controls.input ? controls.input.value : '';
    showNotice(root, 'loading', COPY.loading);
    // A last guard: whatever happens, the loading message never stays.
    const guard = setTimeout(() => { if (noticeFor(root).classList.contains('is-loading')) showNotice(root, 'error', COPY.failed); }, RECORD_TIMEOUT_MS + 2000);
    api.fetchRecord(link.id).then(result => {
      clearTimeout(guard);
      if ((controls.signal && controls.signal.aborted) || !root.isConnected) return;
      if (!result.record) { showNotice(root, 'error', result.missing ? COPY.unavailable : COPY.failed); return; }
      // The shopper started on something else while the deck loaded: leave it.
      if (textarea.value.trim() || (controls.mode && controls.mode() !== startMode) || (controls.input && controls.input.value !== startURL)) { showNotice(root); return; }
      if (sized(result.record.decklist, result.record)) return;
      showNotice(root);
      fill(result.record.decklist, result.record, false);
    }, () => { clearTimeout(guard); showNotice(root, 'error', COPY.failed); });
    return null;
  }
  function setup(root, product, allowed, validURL, controls) {
    if (!allowed || new URL(product, location.origin).pathname !== location.pathname) return null;
    const list = readList(location.href);
    if (list) return setupList(root, list, controls);
    const incoming = read(location.href);
    if (!incoming) return null;
    const key = location.pathname + '\n' + incoming.url + '\n' + incoming.contents;
    const saved = history.state?.trcDeckLink;
    const prior = drafts.get(key) || (saved?.key === key ? saved : null);
    // Never overwrite a field restored by the browser or an existing initializer.
    if (!controls.input.value) controls.input.value = prior ? prior.url : incoming.url;
    if (controls.sideboard) controls.sideboard.value = prior ? prior.contents : incoming.contents;
    controls.setMode(prior ? prior.mode : 'link');
    function remember() {
      const draft = {key, url: controls.input.value, contents: controls.sideboard?.value || 'Mainboard', mode: controls.mode()};
      drafts.set(key, draft);
      try { history.replaceState({...history.state, trcDeckLink: draft}, '', location.href); } catch (_) { /* In-memory restoration still works. */ }
    }
    root.addEventListener('input', remember, {signal: controls.signal});
    root.addEventListener('change', remember, {signal: controls.signal});
    remember();
    return {auto: incoming.auto && validURL(controls.input.value)};
  }
  function initBuilder(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const form = root.querySelector('form');
    const output = root.querySelector('[data-link-output]');
    const code = root.querySelector('[data-button-output]');
    const status = root.querySelector('[data-link-status]');
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      const fields = new FormData(form);
      try {
        const source = new URL(String(fields.get('deck')));
        if (!supportedURL(source.href)) throw new Error('Use a supported public deck URL.');
        const choice = form.querySelector('[name="product"]');
        const option = choice.selectedOptions[0];
        const link = build(root.dataset.storeUrl || location.href, choice.value, source.href, String(fields.get('sacode') || ''), option.dataset.auto === 'true');
        output.value = link;
        const escaped = link.replace(/[&"<>]/g, character => ({'&':'&amp;', '"':'&quot;', '<':'&lt;', '>':'&gt;'}[character]));
        code.value = '<a href="' + escaped + '" rel="sponsored">Buy this deck</a>';
        root.querySelector('[data-link-results]').hidden = false;
        status.textContent = 'Your link is ready. Deck contents are checked when a customer opens it.';
      } catch (error) { status.textContent = error.message; }
    });
    root.querySelectorAll('[data-copy]').forEach(button => button.addEventListener('click', async function () {
      const field = button.dataset.copy === 'link' ? output : code;
      try { await navigator.clipboard.writeText(field.value); status.textContent = 'Copied.'; }
      catch (_) { field.focus(); field.select(); status.textContent = 'Select and copy the highlighted text.'; }
    }));
  }
  const api = {read, readList, destination, build, setup, selectVariant, supportedURL, countList, landingVariant, recordVariant, fetchRecord,
    navigate: url => location.replace(url), copy: COPY};
  window.TRCDeckLinks = api;
  function boot(scope) { scope.querySelectorAll('[data-deck-link-builder]').forEach(initBuilder); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), {once:true});
  else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
